import { Checkbox, Collapse, Spin } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { CollapseDown, CollapseUp } from '../../assets/icons';
import { Button } from '../../components/AntD/Button/Button';
import { Images } from '../../config/images';
import { useAffiliateFAQs } from '../../Hooks/useAffiliateFAQs';
import useLegal from '../../Hooks/useLegal';
import { getReferralLink } from '../../services/authApi/authApi';
import { acceptAgreementApi } from '../../services/authApi/userApi';
import { resetPolicyDetails } from '../../store/reducer/legalSlice';
import { createMarkup } from '../../utils/createMarkup';
import { POLICY_AGREEMENTS } from '../../utils/policy.constants';
import { toastNotify } from '../../utils/toastNotify';
import './AffiliateProgram.scss';
import useGetUserProfile from '../../Hooks/useGetUserProfile';
import ReferralCodeModal from '../../components/ReferralCodeModal/ReferralCodeModal';

const handleKeyActivate = (callback) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    callback(e);
  }
};

const AffiliateProgram = () => {
  const [openSuccessModal, SetOpenSuccessModal] = useState();
  const [isHasInvested, setIsHasInvested] = useState();
  const [isAgreementAccepted, setIsAgreementAccepted] = useState();
  const [isProfileCompleted, setIsProfileCompleted] = useState();
  const [referralCode, setReferralCode] = useState('');
  const [isReferralCodeUpdatedValue, setIsReferralCodeUpdatedValue] = useState(false);
  const [isReferralLink, setIsReferralLink] = useState('');
  const [isCheckboxEnabled, setCheckboxEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  // const { loading: loadingFaq, faqList } = useFAQ();
  const { loading: loadingFaq, affiliateFAQList } = useAffiliateFAQs();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useGetUserProfile();
  const { fetchPolicyByEnum, isFetchingPolicy = true, policyDetails } = useLegal({ shouldFetchTypes: false });

  const { Panel } = Collapse;

  const scrollableDivRef = useRef(null);

  const handleNavigateVerification = () => {
    navigate('/sign-up/verification');
  };

  const handleNavigateInitialMarket = () => {
    navigate('/marketplace/initial');
  };
  const fetchReferralLink = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const response = await getReferralLink(token);
        if (response?.data) {
          const { referralLink, hasInvested, agreementAccepted, profileCompleted, referralCode, isReferralCodeUpdated } = response.data;
          setIsReferralLink(referralLink);
          setIsProfileCompleted(profileCompleted);
          setIsAgreementAccepted(agreementAccepted);
          setIsHasInvested(hasInvested);
          setReferralCode(referralCode);
          setIsReferralCodeUpdatedValue(isReferralCodeUpdated);
          //

          if (hasInvested && agreementAccepted && profileCompleted) {
            localStorage.setItem('referralLink', referralLink);
            return;
          } else {
            localStorage.removeItem('referralLink');
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching referral link:', error);
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollableDiv = scrollableDivRef.current;
      if (scrollableDiv) {
        const { scrollTop, scrollHeight, clientHeight } = scrollableDiv;
        if (scrollTop + clientHeight >= scrollHeight) {
          if (isAgreementAccepted === false) {
            // setCheckboxEnabled(true);
          }
        }
      }
    };

    const scrollableDiv = scrollableDivRef.current;
    if (scrollableDiv) {
      scrollableDiv.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (scrollableDiv) {
        scrollableDiv.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isAgreementAccepted]);
  useEffect(() => {
    fetchReferralLink();
  }, []);

  useEffect(() => {
    fetchPolicyByEnum(POLICY_AGREEMENTS.AFFILIATE_AGREEMENT);

    return () => {
      dispatch(resetPolicyDetails());
    };
  }, []);

  const scrollToSectionGetStarted = () => {
    const section = document.getElementById('complete-profile');
    const header = document.getElementById('account-nav-wrap');
    const warningBarWrap = document.getElementById('warning-bar-wrap');
    const headerHeight = header.offsetHeight === 0 ? 30 : header.offsetHeight;
    const warningBarWrapHeight = warningBarWrap ? warningBarWrap?.offsetHeight : 0;

    if (section && header) {
      const topOffset = headerHeight + warningBarWrapHeight - 20;
      const sectionPosition = section.getBoundingClientRect().top + window.scrollY - topOffset;
      window.scrollTo({
        top: sectionPosition,
        behavior: 'smooth',
      });
    }
  };

  const scrollToSectionReferralPolicy = () => {
    const section = document.getElementById('referral-policy');
    const header = document.getElementById('account-nav-wrap');
    const warningBarWrap = document.getElementById('warning-bar-wrap');
    const headerHeight = header.offsetHeight === 0 ? 30 : header.offsetHeight;
    const warningBarWrapHeight = warningBarWrap ? warningBarWrap?.offsetHeight : 0;

    if (section && header) {
      const topOffset = headerHeight + warningBarWrapHeight;
      const sectionPosition = section.getBoundingClientRect().top + window.scrollY - topOffset;
      window.scrollTo({
        top: sectionPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleAgreeTerms = async (e) => {
    const isChecked = e.target.checked;
    setLoading(true);
    setIsAgreementAccepted(isChecked);
    try {
      if (!isChecked) return;
      const response = await acceptAgreementApi(POLICY_AGREEMENTS.AFFILIATE_AGREEMENT, isChecked);
      if (response?.data?.isError) {
        toastNotify('error', response?.data?.message);
      } else {
        toastNotify('success', 'Agreement accepted successfully!');
        setCheckboxEnabled(false);
        fetchReferralLink();
      }
    } catch (error) {
      toastNotify('error', error?.response?.data?.message ?? 'Something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='affiliate'>
      <div className='affiliate__head'>
        <LazyLoadImage src={Images.affiliateIcon.default} alt='RealBricks Referral Program' />
        <div>
          <h3 className='mb-0'>
            RealBricks <span>Referral Program</span>
          </h3>
          <p>
            <strong>Invite a friend, get $50 of real estate.</strong>
          </p>
        </div>
      </div>
      <div className='affiliate__how-work'>
        <h4 className='mb-24'>How it works</h4>
        <div className='how-work-details mb-24'>
          <h5 className='how-work-details__title fw-700 text-almost-black'>Apply</h5>
          <div className='how-work-details__info'>
            <p>
              Start by{' '}
              <span
                onClick={handleNavigateVerification}
                onKeyDown={handleKeyActivate(handleNavigateVerification)}
                role='button'
                tabIndex={0}
                className='help-center__section__link'
                style={{ cursor: 'pointer', textDecoration: 'underline', color: '#8a4743' }}
              >
                completing your profile,
              </span>{' '}
              reading and accepting the{' '}
              <span
                onClick={scrollToSectionReferralPolicy}
                onKeyDown={handleKeyActivate(scrollToSectionReferralPolicy)}
                role='button'
                tabIndex={0}
                className='help-center__section__link'
                style={{ cursor: 'pointer', textDecoration: 'underline', color: '#8a4743' }}
              >
                referral agreement,
              </span>{' '}
              and are invested in at least one property on the Realbricks platform.
            </p>
          </div>
        </div>
        <div className='how-work-details mb-24'>
          <h5 className='how-work-details__title fw-700 text-almost-black'>Generate</h5>
          <div className='how-work-details__info'>
            <p>
              Create your custom referral link to share with your friends, family,or audience. This can be used as both a link or a referral code at signup and checkout.{' '}
            </p>
          </div>
        </div>
        <div className='how-work-details mb-24'>
          <h5 className='how-work-details__title fw-700 text-almost-black'>Earn</h5>
          <div className='how-work-details__info'>
            <p>
              Each time someone signs up and makes their first investment using your link, you'll each receive $50 worth of shares in the property they invest in.{' '}
            </p>
          </div>
        </div>
        <Button onClick={scrollToSectionGetStarted} type='primary' label='Get Started' block />
      </div>
      <div id='complete-profile' className='affiliate__profile-step pt-40'>
        <div className='profile-step-card'>
          <div className='profile-step-card__head'>
            <h4 className='mb-0'>Step 1: Complete Your Profile </h4>
            {isProfileCompleted ? (
              <span className='status-tag success-text fw-600 fz-18 d-flex'>
                {/* <CheckCircle /> Completed */}
                Completed
              </span>
            ) : (
              <span className='status-tag text-error fw-600 fz-18 d-flex'>
                {/* <CloseIcon color/> Incomplete */}
                Incomplete
              </span>
            )}
          </div>
          <div className='profile-step-card__details'>
            <p>
              If you haven't already -{' '}
              <span
                onClick={handleNavigateVerification}
                onKeyDown={handleKeyActivate(handleNavigateVerification)}
                role='button'
                tabIndex={0}
                className='help-center__section__link'
                style={{ cursor: 'pointer', textDecoration: 'underline', color: '#8a4743' }}
              >
                Complete your profile here,
              </span>{' '}
            </p>
          </div>
        </div>
        <Spin spinning={loading || isFetchingPolicy}>
          <div className='profile-step-card' id='referral-policy'>
            <div className='profile-step-card__head'>
              <h4 className='mb-0'>Step 2: Read and Sign Referral Agreement </h4>
              {isAgreementAccepted ? (
                <span className='status-tag success-text fw-600 fz-18 d-flex'>
                  {/* <CheckCircle /> Completed */}
                  Completed
                </span>
              ) : (
                <span className='status-tag text-error fw-600 fz-18 d-flex'>
                  {/* <CloseIcon color/> Incomplete */}
                  Incomplete
                </span>
              )}
            </div>
            <div className='profile-step-card__details'>
              <p>Please review the terms and conditions and confirm by checking the box at the end.</p>
            </div>
            <div className='profile-step-card__details'>
              <div ref={scrollableDivRef} className='content-overflow-wrap'>
                <div dangerouslySetInnerHTML={{ __html: policyDetails?.content }} />
              </div>
              <Checkbox
                className='mt-16 mb-16 fz-16'
                onChange={handleAgreeTerms}
                checked={isAgreementAccepted}
                disabled={isAgreementAccepted || !isCheckboxEnabled}
              >
                I have read and accept the{' '}
                <Link to='/account/legal#Terms%20of%20use' className='help-center__section__link'>
                  Terms of Use
                </Link>
                , the{' '}
                <Link to='/account/legal#Regulation%20A%20Investment%20Disclosure' className='help-center__section__link'>
                  Regulation A Investment Disclosure
                </Link>
                , and{' '}
                <Link to='/account/legal#Privacy%20Policy' className='help-center__section__link'>
                  Privacy Policy
                </Link>
              </Checkbox>
            </div>
          </div>
        </Spin>
        <div className='profile-step-card'>
          <div className='profile-step-card__head'>
            <h4 className='mb-0'>Step 3: Invest in a Realbricks Property </h4>
            {isHasInvested ? (
              <span className='status-tag success-text fw-600 fz-18 d-flex'>
                {/* <CheckCircle /> Completed */}
                Completed
              </span>
            ) : (
              <span className='status-tag text-error fw-600 fz-18 d-flex'>
                {/* <CloseIcon color/> Incomplete */}
                Incomplete
              </span>
            )}
          </div>
          <div className='profile-step-card__details'>
            <p>
              To be eligible for the referral program, make sure you have at least one investment in a Realbricks property.{' '}
              <span
                onClick={handleNavigateInitialMarket}
                onKeyDown={handleKeyActivate(handleNavigateInitialMarket)}
                role='button'
                tabIndex={0}
                className='help-center__section__link'
                style={{ cursor: 'pointer', textDecoration: 'underline', color: '#8a4743' }}
              >
                Click here to invest.
              </span>{' '}
            </p>
          </div>
        </div>

        <div className='mt-24'>
          <Button
            onClick={() => {
              localStorage.setItem('referralLink', isReferralLink);
              SetOpenSuccessModal(true);
            }}
            type='primary'
            block
            label='Generate Referral Link'
            disabled={!isProfileCompleted || !isAgreementAccepted || !isHasInvested}
          />
        </div>
      </div>
      <div className='pt-40'>
        <h4 className='mb-24'>Frequently Asked Questions</h4>
        <Spin spinning={loadingFaq}>
          {affiliateFAQList?.length ? (
            <Collapse
              accordion
              defaultActiveKey={affiliateFAQList?.length > 0 ? [affiliateFAQList?.[0]?.id] : []}
              expandIconPosition='end'
              expandIcon={({ isActive }) => (isActive ? <CollapseDown width='15' /> : <CollapseUp width='15' />)}
            >
              {affiliateFAQList?.map((faq) => (
                <Panel header={faq?.question} key={faq?.id}>
                  <div dangerouslySetInnerHTML={createMarkup(faq?.content)}></div>
                </Panel>
              ))}
            </Collapse>
          ) : (
            <>{!loadingFaq && <div>No Data Available</div>}</>
          )}
        </Spin>
      </div>
      <ReferralCodeModal
        open={openSuccessModal}
        onClose={() => SetOpenSuccessModal(false)}
        referralCode={referralCode}
        showForm={!isReferralCodeUpdatedValue}
        refetchReferralLink={fetchReferralLink}
        referralLink={isReferralLink}
      />
    </div>
  );
};
export default AffiliateProgram;
